import { useNavigate } from "react-router-dom";
import { AlertTriangle, Gauge, CalendarClock, ShieldAlert, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui-kit/Button";

const ICONS = {
  invoice_overdue: AlertTriangle,
  acsa_cap_fill: Gauge,
  acsa_coverage_gap: ShieldAlert,
  billing_cycle_due: CalendarClock,
};

// Severity drives the box color (fill + left accent + icon); text stays near-black
// ink (theme foreground) for maximum legibility on the soft tint.
const SEVERITY = {
  info: { row: "border-send/40 border-l-send bg-send/20", icon: "bg-send/30 text-send" },
  amber: { row: "border-status-partial/40 border-l-status-partial bg-status-partial/20", icon: "bg-status-partial/30 text-status-partial" },
  red: { row: "border-status-void/40 border-l-status-void bg-status-void/20", icon: "bg-status-void/30 text-status-void" },
};

export function NotificationList({ items }) {
  const navigate = useNavigate();

  if (!items.length) {
    return (
      <div className="grid place-items-center gap-2 rounded-lg border border-dashed border-border bg-surface py-14 text-center" data-testid="notifications-empty">
        <CheckCircle2 className="h-8 w-8 text-status-paid" />
        <p className="text-sm font-semibold text-foreground">You&apos;re all caught up</p>
        <p className="max-w-sm text-sm text-muted-foreground">No coverage caps are full, no billing cycles are due, and no invoices are overdue.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="notifications-list">
      {items.map((n) => {
        const Icon = ICONS[n.type] || AlertTriangle;
        const sev = SEVERITY[n.severity] || SEVERITY.info;
        return (
          <div
            key={n.id}
            data-testid={`notification-${n.type}`}
            data-severity={n.severity}
            className={`flex gap-4 rounded-lg border border-l-4 ${sev.row} p-4 shadow-card transition-shadow hover:shadow-pop`}
          >
            <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-md ${sev.icon}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground" data-testid="notification-title">{n.title}</p>
              <p className="mt-0.5 text-sm text-foreground/80" data-testid="notification-message">{n.message}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {n.actions.map((a) => (
                  <Button
                    key={a.testid}
                    size="sm"
                    variant={a.variant || "secondary"}
                    data-testid={a.testid}
                    onClick={() => navigate(a.to)}
                  >
                    {a.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
